import React, { useState, useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import axios from 'axios';
import {
  Input, Popconfirm, Empty, Spin, Tooltip,
} from 'antd';
import { Plus, Trash, MagnifyingGlass } from '@phosphor-icons/react';
import { message } from './antd';
import Typography from '../../components/Typography';
import Button from '../../components/Button';
import csrfHeaders from '../../utils/csrfHeaders';
import { captureAnalytics } from '../../actions/preload';
import {
  normalizeNpiToken,
  isValidNpiToken,
  normalizeFaxToken,
  isValidFaxToken,
  faxLocalDigits,
  formatFaxDisplay,
} from '../../utils/identifierTokens';

const ENDPOINT = '/api/v1/portal/partner_identifiers';

const errorMessage = (error, fallback) => (
  error?.response?.data?.error || fallback
);

// A filter field only earns its place once a list is long enough to be hard
// to scan; below this it would just be clutter.
const FILTER_THRESHOLD = 10;

const IdentifierList = ({
  title, description, placeholder, items, formatItem, normalizeInput, isValidToken,
  invalidHint, duplicateHint, lastItemLockedMessage, onAdd, onRemove,
}) => {
  const [value, setValue] = useState('');
  const [adding, setAdding] = useState(false);
  const [query, setQuery] = useState('');

  const tokenValid = isValidToken(value);
  // A valid value that matches an existing item is a no-op add; disable Add and
  // explain why rather than silently doing nothing.
  const isDuplicate = tokenValid && items.some((item) => faxLocalDigits(item) === faxLocalDigits(value));
  const lastItemLocked = Boolean(lastItemLockedMessage) && items.length === 1;

  const searchable = items.length > FILTER_THRESHOLD;
  const queryKey = faxLocalDigits(query);
  const visibleItems = query.trim()
    ? items.filter((item) => faxLocalDigits(item).includes(queryKey))
    : items;

  const handleAdd = async () => {
    if (adding || !tokenValid || isDuplicate) return;
    setAdding(true);
    const ok = await onAdd(value);
    setAdding(false);
    if (ok) setValue('');
  };

  return (
    <div className="portal-settings__identifier-card">
      <Typography variant="subtitleTwo" bold>{title}</Typography>
      <Typography variant="text" size="small" className="portal-settings__subtitle">
        {description}
      </Typography>

      <div className="portal-settings__identifier-input">
        <Input
          value={value}
          placeholder={placeholder}
          onChange={(e) => setValue(normalizeInput(e.target.value))}
          onPressEnter={handleAdd}
        />
        <Button
          type="primary"
          size="small"
          label="Add"
          leftIcon={<Plus size={16} weight="bold" />}
          disabled={adding || !tokenValid || isDuplicate}
          onClick={handleAdd}
        />
      </div>

      {Boolean(invalidHint) && value.length > 0 && !tokenValid && (
        <Typography variant="text" size="small" className="portal-settings__hint portal-settings__hint--input">
          {invalidHint}
        </Typography>
      )}

      {isDuplicate && (
        <Typography variant="text" size="small" className="portal-settings__hint portal-settings__hint--input">
          {duplicateHint}
        </Typography>
      )}

      {searchable && (
        <Input
          className="portal-settings__identifier-filter"
          value={query}
          allowClear
          placeholder={`Filter ${title}`}
          prefix={<MagnifyingGlass size={14} />}
          onChange={(e) => setQuery(e.target.value)}
        />
      )}

      {items.length === 0 ? (
        <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="None added yet" />
      ) : (
        <ul className="portal-settings__chip-list">
          {query.trim() && visibleItems.length === 0 && (
            <li className="portal-settings__chip-empty">
              <Typography variant="text" size="small">{`No ${title} match`}</Typography>
            </li>
          )}
          {visibleItems.map((item) => (
            <li key={item} className="portal-settings__chip">
              <span>{formatItem ? formatItem(item) : item}</span>
              {lastItemLocked ? (
                <Tooltip title={lastItemLockedMessage}>
                  {/* Disabled buttons swallow pointer events; the span keeps the tooltip working. */}
                  <span>
                    <button type="button" className="portal-settings__icon-btn" aria-label="Remove" disabled>
                      <Trash size={16} />
                    </button>
                  </span>
                </Tooltip>
              ) : (
                <Popconfirm
                  title="Remove this identifier? Referrals matching it will stop routing to you."
                  okText="Remove"
                  okButtonProps={{ danger: true }}
                  onConfirm={() => onRemove(item)}
                >
                  <button type="button" className="portal-settings__icon-btn" aria-label="Remove">
                    <Trash size={16} />
                  </button>
                </Popconfirm>
              )}
            </li>
          ))}
        </ul>
      )}

      {lastItemLocked && (
        <Typography variant="text" size="small" className="portal-settings__hint portal-settings__hint--lock">
          {lastItemLockedMessage}
        </Typography>
      )}
    </div>
  );
};

IdentifierList.propTypes = {
  title: PropTypes.string.isRequired,
  description: PropTypes.string.isRequired,
  placeholder: PropTypes.string.isRequired,
  items: PropTypes.arrayOf(PropTypes.string).isRequired,
  formatItem: PropTypes.func,
  normalizeInput: PropTypes.func.isRequired,
  isValidToken: PropTypes.func.isRequired,
  invalidHint: PropTypes.string,
  duplicateHint: PropTypes.string,
  lastItemLockedMessage: PropTypes.string,
  onAdd: PropTypes.func.isRequired,
  onRemove: PropTypes.func.isRequired,
};

IdentifierList.defaultProps = {
  formatItem: null,
  invalidHint: null,
  duplicateHint: null,
  lastItemLockedMessage: null,
};

const PortalRoutingIdentifiers = () => {
  const [npis, setNpis] = useState([]);
  const [faxNumbers, setFaxNumbers] = useState([]);
  const [loading, setLoading] = useState(true);

  const applyPayload = (data) => {
    setNpis(data.npis || []);
    setFaxNumbers(data.fax_numbers || []);
  };

  const fetchIdentifiers = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(ENDPOINT);
      applyPayload(data);
    } catch (error) {
      message.error(errorMessage(error, 'Failed to load routing identifiers.'));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchIdentifiers();
  }, [fetchIdentifiers]);

  const addIdentifier = async (type, value) => {
    try {
      const { data } = await axios.post(ENDPOINT, { type, value }, { headers: csrfHeaders() });
      applyPayload(data);
      captureAnalytics('portal_identifier_added', { identifier_type: type });
      return true;
    } catch (error) {
      message.error(errorMessage(error, 'Failed to add.'));
      return false;
    }
  };

  const removeIdentifier = async (type, value) => {
    try {
      const { data } = await axios.delete(ENDPOINT, { data: { type, value }, headers: csrfHeaders() });
      applyPayload(data);
      captureAnalytics('portal_identifier_removed', { identifier_type: type });
    } catch (error) {
      message.error(errorMessage(error, 'Failed to remove.'));
    }
  };

  return (
    <div className="portal-settings__section">
      <div className="portal-settings__section-header">
        <div>
          <Typography variant="subtitleOne" bold>Routing Identifiers</Typography>
          <Typography variant="text" size="small" className="portal-settings__subtitle">
            NPIs and fax numbers control which incoming referrals route to your organization.
            Changes apply across all your referring partners and take effect immediately.
          </Typography>
        </div>
      </div>

      {loading ? (
        <div className="portal-settings__loading"><Spin /></div>
      ) : (
        <div className="portal-settings__identifier-grid">
          <IdentifierList
            title="NPIs"
            description="10-digit National Provider Identifiers for your providers."
            placeholder="1234567890"
            items={npis}
            normalizeInput={normalizeNpiToken}
            isValidToken={isValidNpiToken}
            invalidHint="NPIs are exactly 10 digits."
            duplicateHint="This NPI is already added."
            onAdd={(value) => addIdentifier('npi', value)}
            onRemove={(value) => removeIdentifier('npi', value)}
          />
          <IdentifierList
            title="Fax Numbers"
            description="US fax numbers referrals may arrive from."
            placeholder="(555) 123-4567"
            items={faxNumbers}
            formatItem={formatFaxDisplay}
            normalizeInput={normalizeFaxToken}
            isValidToken={isValidFaxToken}
            invalidHint="Enter a 10-digit US fax number."
            duplicateHint="This fax number is already added."
            lastItemLockedMessage="Add another fax number first, then you can remove this one."
            onAdd={(value) => addIdentifier('fax_number', value)}
            onRemove={(value) => removeIdentifier('fax_number', value)}
          />
        </div>
      )}
    </div>
  );
};

export default PortalRoutingIdentifiers;
